import { CAPSULE_HALF_HEIGHT, PLAYER_RADIUS, STEP_DT, KCC_OFFSET, GRAVITY, STEP_MAX_HEIGHT, STEP_MIN_WIDTH, SNAP_TO_GROUND, MAX_SLOPE_RAD, GRENADE_LINEAR_DAMPING, GRENADE_ANGULAR_DAMPING, } from "./tuning.js";
// Interaction groups: upper 16 bits membership, lower 16 bits filter.
export const GROUP_MAP = 0x0001;
export const GROUP_PLAYER = 0x0002;
export const GROUP_PROJECTILE = 0x0004;
export const groups = (membership, filter) => ((membership << 16) | filter) >>> 0;
/** What the character controller may collide with when moving. Players pass through each other in v1. */
export const KCC_QUERY_GROUPS = groups(GROUP_PLAYER, GROUP_MAP);
/** Rays that ask "is there a wall in the way". */
export const MAP_RAY_GROUPS = groups(0xffff, GROUP_MAP);
function eulerToQuat(rx, ry, rz) {
    const cx = Math.cos(rx / 2), sx = Math.sin(rx / 2);
    const cy = Math.cos(ry / 2), sy = Math.sin(ry / 2);
    const cz = Math.cos(rz / 2), sz = Math.sin(rz / 2);
    return {
        x: sx * cy * cz + cx * sy * sz,
        y: cx * sy * cz - sx * cy * sz,
        z: cx * cy * sz - sx * sy * cz,
        w: cx * cy * cz + sx * sy * sz,
    };
}
export function createEnv(R, map) {
    // World gravity drives grenades and knives. Players are kinematic and integrate their own gravity in the step.
    const world = new R.World({ x: 0, y: -GRAVITY, z: 0 });
    world.timestep = STEP_DT;
    for (const b of map) {
        const desc = R.RigidBodyDesc.fixed().setTranslation(b.x, b.y, b.z);
        if (b.rx || b.ry || b.rz)
            desc.setRotation(eulerToQuat(b.rx ?? 0, b.ry ?? 0, b.rz ?? 0));
        const body = world.createRigidBody(desc);
        world.createCollider(R.ColliderDesc.cuboid(b.hx, b.hy, b.hz).setCollisionGroups(groups(GROUP_MAP, 0xffff)).setFriction(0.8), body);
    }
    const controller = world.createCharacterController(KCC_OFFSET);
    controller.enableAutostep(STEP_MAX_HEIGHT, STEP_MIN_WIDTH, false);
    controller.enableSnapToGround(SNAP_TO_GROUND);
    controller.setSlideEnabled(true);
    controller.setMaxSlopeClimbAngle(MAX_SLOPE_RAD);
    controller.setApplyImpulsesToDynamicBodies(false);
    const events = new R.EventQueue(true);
    // One step builds the query structures for the static map.
    world.step(events);
    return { R, world, controller, events };
}
export function createPlayerBody(env, x, y, z) {
    const { R, world } = env;
    const body = world.createRigidBody(R.RigidBodyDesc.kinematicPositionBased().setTranslation(x, y, z));
    const collider = world.createCollider(R.ColliderDesc.capsule(CAPSULE_HALF_HEIGHT, PLAYER_RADIUS)
        .setCollisionGroups(groups(GROUP_PLAYER, GROUP_MAP | GROUP_PROJECTILE))
        .setActiveEvents(R.ActiveEvents.COLLISION_EVENTS), body);
    return { body, collider };
}
export function removePlayerBody(env, pb) {
    env.world.removeCollider(pb.collider, false);
    env.world.removeRigidBody(pb.body);
}
/** A grenade or a thrown knife: a small ball that bounces off the map and players. */
export function createProjectile(env, o) {
    const { R, world } = env;
    const body = world.createRigidBody(R.RigidBodyDesc.dynamic()
        .setTranslation(o.x, o.y, o.z)
        .setLinvel(o.vx, o.vy, o.vz)
        .setLinearDamping(GRENADE_LINEAR_DAMPING)
        .setAngularDamping(GRENADE_ANGULAR_DAMPING)
        .setCcdEnabled(true));
    const collider = world.createCollider(R.ColliderDesc.ball(o.radius)
        .setRestitution(o.restitution)
        .setFriction(o.friction)
        .setDensity(2.0)
        .setCollisionGroups(groups(GROUP_PROJECTILE, GROUP_MAP | GROUP_PLAYER))
        .setActiveEvents(R.ActiveEvents.COLLISION_EVENTS), body);
    return { body, collider };
}
export function removeProjectile(env, pb) {
    env.world.removeCollider(pb.collider, false);
    env.world.removeRigidBody(pb.body);
}
/** True when a straight line from a to b crosses map geometry. */
export function blocked(env, ax, ay, az, bx, by, bz) {
    const dx = bx - ax, dy = by - ay, dz = bz - az;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-4)
        return false;
    const ray = new env.R.Ray({ x: ax, y: ay, z: az }, { x: dx / len, y: dy / len, z: dz / len });
    const hit = env.world.castRay(ray, len, true, undefined, MAP_RAY_GROUPS);
    return hit !== null;
}
/** Distance along a unit direction to the first map surface, or null when nothing is within maxLen. */
export function rayDistance(env, x, y, z, dx, dy, dz, maxLen) {
    const ray = new env.R.Ray({ x, y, z }, { x: dx, y: dy, z: dz });
    const hit = env.world.castRay(ray, maxLen, true, undefined, MAP_RAY_GROUPS);
    return hit ? hit.timeOfImpact : null;
}
const IDENTITY_ROT = { x: 0, y: 0, z: 0, w: 1 };
const ballCache = new Map();
/**
 * Sweep a ball of `radius` along a unit direction and report the first map surface it touches.
 * `toi` is how far the centre may travel before contact, so the caller must not subtract the
 * radius again. A ray cannot stand in for this: a ray misses a grazing pass that the ball's
 * shoulder would clip, which is exactly the case that made predicted grenades disagree with the
 * server's own body. The normal comes back in world space, pointing back at the ball.
 */
export function sweepBall(env, x, y, z, dx, dy, dz, maxLen, radius) {
    let shape = ballCache.get(radius);
    if (!shape) {
        shape = new env.R.Ball(radius);
        ballCache.set(radius, shape);
    }
    const hit = env.world.castShape({ x, y, z }, IDENTITY_ROT, { x: dx, y: dy, z: dz }, shape, 0, maxLen, true, undefined, MAP_RAY_GROUPS);
    if (!hit)
        return null;
    const n = hit.normal1;
    return { toi: hit.time_of_impact, nx: n.x, ny: n.y, nz: n.z };
}
export function freeEnv(env) {
    env.world.removeCharacterController(env.controller);
    env.events.free();
    env.world.free();
}
//# sourceMappingURL=physics.js.map